import React, { useEffect } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { required } from "common/validate";

import { InteractiveTooltipWrapper } from "schedule-v2/components";
import { ExlyDropdown, ExlyInput } from "common/form";
import { Switch } from "@my-scoot/component-library-legacy";
import { ReactComponent as TipsAndUpdatesIcon } from "assets/vectors/tips_and_updates.svg";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import useStyles from "../../../schedule-v2.module";
import classnames from "classnames";
import {
  answer_types,
  question_initial_state,
  question_placement_types,
} from "./questionFormConstants";
import {
  questions_type_mapping,
  questions_placement_mapping,
  KYC_QUESTIONS_COLUMNS_CONFIG,
  QUESTION_TYPE_ID_FEATURE_KEY_MAPPING,
} from "constants/customerQuestions.constants";
import { isMultiQuestionType } from "ui/pages/QuestionsPage/utils/QuestionsPage.utils";
import {
  getDisplayStyleOptions,
  QUESTION_DISPLAY_STYLE,
  QUESTION_FORM_KEYS,
  QUESTION_TYPE_IDS,
} from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/common/QuestionFormFields/constants/QuestionFormFields.constants";
import { FormFieldLabel } from "features/Common/modules/Form/modules/FormFieldLabel/FormFieldLabel";
import { MultiCheckBoxInputTable } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/common/MultiCheckBoxInputTable/MultiCheckBoxInputTable";
import { getFormValueByPath } from "features/Common/modules/Form/Form.utils";
import { getMultiQuestionsWithDisabledState } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/common/QuestionFormFields/utils/QuestionFormFields.utils";
import { useQuestionFields } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/common/QuestionFormFields/hooks/useQuestionFields";
import { CheckCreditExpiry } from "common/Components/UBP/CheckCreditExpiry";

const QuestionsForms = (props) => {
  const formState = useFormState();
  const form = useForm();
  const is_desktop = useDesktopMediaQuery();
  const classes = useStyles({ is_desktop });
  // If Multi Type Question then we will not allow user to edit these custom Questions
  const question = formState?.values?.questionObj;
  const questionType = formState?.values?.questionObj?.type;
  const isQuestionMultiType = isMultiQuestionType(questionType);
  const { handleFieldsChange, handleTypeChange } = useQuestionFields({ form });
  const showCreditExpiryAlert =
    isQuestionMultiType && QUESTION_TYPE_ID_FEATURE_KEY_MAPPING?.[questionType];

  useEffect(() => {
    if (props?.record) {
      const edit_state = {
        question: props?.record?.question,
        type: props?.record?.type,
        question_placement: props?.record?.question_placement,
        is_mandatory: props?.record?.is_mandatory,
        choices: props?.record?.choices,
        validations: props?.record?.validations,
      };

      form.change("questionObj", { ...edit_state });
    } else {
      form.change("questionObj", { ...question_initial_state });
    }
  }, []);

  const renderTypeSpecificValidation = (question) => {
    if (
      question?.type &&
      questions_type_mapping[question?.type]?.typeSpecificValidation
    ) {
      return questions_type_mapping[question?.type]?.typeSpecificValidation(
        question
      );
    }
    return null;
  };

  const multiQuestionsList = getMultiQuestionsWithDisabledState(
    getFormValueByPath(
      formState?.values,
      QUESTION_FORM_KEYS.multi_questions.key
    ) ?? []
  );

  return (
    <>
      {showCreditExpiryAlert && (
        <CheckCreditExpiry
          feature_key={QUESTION_TYPE_ID_FEATURE_KEY_MAPPING[questionType]}
          className="tw--mx-6 tw-w-[calc(100%+48px)]"
          warningClosable={false}
        />
      )}
      <InteractiveTooltipWrapper>
        <div className={classes.fieldWrapper}>
          <div
            className={`${classes.questionsTitle} tw-font-medium tw-text-sm`}
          >
            Question <span className="tw-text-red-500">*</span>
          </div>
          <Field
            name="questionObj.question"
            placeholder="Type in your question..."
            size="small"
            style={{ width: "100%" }}
            component={ExlyInput}
          />
        </div>
        <div className={classes.fieldWrapper}>
          <div
            className={`${classes.questionsTitle} tw-font-medium tw-text-sm`}
          >
            Answer Type <span className="tw-text-red-500">*</span>
          </div>
          <Field
            name={QUESTION_FORM_KEYS.type.key}
            fullWidth="100%"
            options={answer_types()}
            size="small"
            style={{ width: "100%" }}
            component={ExlyDropdown}
            onChange={handleTypeChange}
          />
        </div>

        {isQuestionMultiType && (
          <Field
            name={QUESTION_FORM_KEYS.multi_questions.key}
            placeholder={QUESTION_FORM_KEYS.question.placeholder}
            label={
              <FormFieldLabel
                formKeys={QUESTION_FORM_KEYS}
                formKey={QUESTION_FORM_KEYS.question.labelKey}
                classes={{ spanClassName: "tw-mr-1" }}
                required
              />
            }
            size="small"
            component={MultiCheckBoxInputTable}
            validate={required}
            fullWidth
            multiQuestions={multiQuestionsList}
            columns={KYC_QUESTIONS_COLUMNS_CONFIG}
            onChange={(value, updatedRow, updatedKey) =>
              handleFieldsChange({
                key: QUESTION_FORM_KEYS.multi_questions.key,
                value,
                updatedSubKey: updatedKey,
                question,
              })
            }
          />
        )}

        {renderTypeSpecificValidation(formState?.values?.questionObj)}
        {[
          QUESTION_TYPE_IDS.SINGLE_SELECT_DROPDOWN,
          QUESTION_TYPE_IDS.MULTI_SELECT_DROPDOWN,
        ].includes(questionType) && (
          <div className={classes.fieldWrapper}>
            <div
              className={`${classes.questionsTitle} tw-font-medium tw-text-sm`}
            >
              Display as
            </div>
            <Field
              name={QUESTION_FORM_KEYS.display_style.key}
              fullWidth="100%"
              options={getDisplayStyleOptions(questionType)}
              size="small"
              style={{ width: "100%" }}
              component={ExlyDropdown}
              defaultValue={QUESTION_DISPLAY_STYLE.DROPDOWN}
              format={(value) => value || QUESTION_DISPLAY_STYLE.DROPDOWN}
              parse={(value) => value || QUESTION_DISPLAY_STYLE.DROPDOWN}
            />
          </div>
        )}
        <div className={classes.fieldWrapper}>
          <div className={classes.questionsTitle}>Question Placement</div>
          <Field
            name="questionObj.question_placement"
            fullWidth="100%"
            options={question_placement_types}
            size="small"
            style={{ width: "100%" }}
            component={ExlyDropdown}
          />
        </div>
        <div className={classes.switchWrapper}>
          <Switch
            checked={
              formState?.values?.questionObj?.is_mandatory
                ? formState?.values?.questionObj?.is_mandatory
                : false
            }
            size="large"
            onChange={() => {
              handleFieldsChange({
                key: QUESTION_FORM_KEYS.is_mandatory.key,
                value: !formState?.values?.questionObj?.is_mandatory,
                question,
              });
            }}
            color="success"
          />
          <span>Is Mandatory</span>
        </div>
        {formState?.values?.questionObj?.question_placement ===
          questions_placement_mapping.before_booking && (
          <div className={classnames(classes.questionsTip, classes.tipPadding)}>
            <div className={!is_desktop && classes.svgWrapper}>
              <TipsAndUpdatesIcon />
            </div>
            <div className={is_desktop && classes.textWrapper}>
              Some customers may drop off due to this additional step. Please
              ensure that the questions are important{" "}
            </div>
          </div>
        )}
      </InteractiveTooltipWrapper>
    </>
  );
};

export default QuestionsForms;
